import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Dices, Gift, Tv } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const NotFound: React.FC = () => {
  const { t } = useTranslation();

  const popularPages = [
    { path: '/', label: t('support:notFound.homePage'), icon: Home, description: t('support:notFound.homeDescription') },
    { path: '/slotlar', label: 'Slotlar', icon: Dices, description: t('support:notFound.slotsDescription') },
    { path: '/canli-casino', label: 'Canlı Casino', icon: Tv, description: t('support:notFound.liveCasinoDescription') },
    { path: '/etkinlikler/promosyonlar', label: t('support:notFound.promotionsLabel'), icon: Gift, description: t('support:notFound.promotionsDescription') }
  ];

  return (
    <>
      <SEO
        title={t('support:notFound.seoTitle')}
        description={t('support:notFound.seoDescription')}
        keywords="404, sayfa bulunamadı, JojoBet, casino"
        url="/404"
      />
      <div className="min-h-screen pt-20 bg-dark-900 flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <h1 className="text-9xl md:text-[12rem] font-gaming font-bold text-gradient bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 bg-clip-text text-transparent mb-4">
              {t('support:notFound.title')}
            </h1>
            <h2 className="text-3xl md:text-4xl font-gaming font-bold text-white mb-4">
              {t('support:notFound.pageNotFound')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              {t('support:notFound.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-12"
          >
            <h3 className="text-2xl font-gaming font-bold text-white mb-8">
              {t('support:notFound.popularPages')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularPages.map((page, index) => {
                const Icon = page.icon;
                return (
                  <motion.div
                    key={page.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                  >
                    <Link
                      to={page.path}
                      className="block p-6 bg-dark-700/50 backdrop-blur-sm rounded-xl border border-dark-600 hover:border-primary-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/10"
                    >
                      <Icon className="w-8 h-8 text-primary-400 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-white mb-2">
                        {page.label}
                      </h4>
                      <p className="text-gray-400 text-sm">
                        {page.description}
                      </p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-full hover:scale-105 transition-all duration-300 transform hover:-translate-y-1"
            >
              <Home className="w-5 h-5 mr-3" />
              {t('support:notFound.backToHome')}
            </Link>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark-700/50 backdrop-blur-sm border border-dark-600 rounded-full hover:scale-105 transition-all duration-300 transform hover:-translate-y-1"
            >
              <ArrowLeft className="w-5 h-5 mr-3" />
              {t('support:notFound.goBack')}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-12 p-6 bg-dark-700/30 rounded-xl border border-dark-600"
          >
            <h4 className="text-lg font-semibold text-white mb-3">
              {t('support:notFound.needHelp')}
            </h4>
            <p className="text-gray-400 mb-4">
              {t('support:notFound.needHelpDesc')}
            </p>
            <Link
              to="/diger/canli-destek"
              className="inline-flex items-center text-primary-400 hover:text-primary-300 transition-colors"
            >
              {t('support:liveSupport.title')}
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
